import { motion, AnimatePresence } from 'framer-motion';
import { X, Copy, Check } from 'lucide-react';
import { useState, useMemo } from 'react';
import DOMPurify from 'dompurify';

interface MessageReceivedModalProps {
  isOpen: boolean;
  from: string;
  htmlContent: string;
  onClose: () => void;
}

export function MessageReceivedModal({
  isOpen,
  from,
  htmlContent,
  onClose,
}: MessageReceivedModalProps) {
  const [copied, setCopied] = useState(false);
  
  // Sanitize HTML content to prevent XSS attacks
  const sanitizedContent = useMemo(
    () => DOMPurify.sanitize(htmlContent),
    [htmlContent]
  );

  const handleCopy = async () => {
    // Create a temporary div to extract text content
    const temp = document.createElement('div');
    temp.innerHTML = htmlContent;
    const textContent = temp.textContent || temp.innerText || '';
    
    try {
      await navigator.clipboard.writeText(textContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
          />

          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', duration: 0.3 }}
              className="w-full max-w-2xl bg-gray-900 rounded-2xl shadow-2xl border border-gray-700 overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-700">
                <div>
                  <h2 className="text-xl font-bold text-white">Message Received</h2>
                  <p className="text-sm text-gray-400 mt-1">
                    From {from}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-800 rounded-lg"
                  aria-label="Close message"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Message Content */}
              <div 
                className="p-6 bg-gray-800/30 text-white max-h-[400px] overflow-y-auto prose prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: sanitizedContent }}
              />

              {/* Footer */}
              <div className="px-6 py-4 bg-gray-800/50 border-t border-gray-700 flex gap-3">
                <button
                  onClick={handleCopy}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium rounded-lg transition-all"
                >
                  {copied ? (
                    <>
                      <Check size={18} />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy size={18} />
                      Copy Text
                    </>
                  )}
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium rounded-lg transition-all"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
